import React, { memo } from 'react';
import { useIntl } from 'react-intl';

import { copy } from '../../../utils/copy';
import { useToast } from '../../hooks/useToast';
import { IconButton } from '../../IconButton/IconButton';
import { Button } from '../Button';

interface Props {
  copyText: string;
  size?: 's' | 'm';
  copyTooltip?: string;
  copiedToastMessage?: string;
  disabled?: boolean;
  disableText?: boolean;
}

export const CopyButton = memo(
  ({
    copyText,
    size = 'm',
    copyTooltip,
    copiedToastMessage,
    disabled,
    disableText,
  }: Props) => {
    const { toast } = useToast();
    const intl = useIntl();

    const defaultCopyTooltip = intl.formatMessage({
      id: 'CopyButton.copyTooltip',
      defaultMessage: 'Copy',
      description: 'Tooltip text for copy button',
    });

    const defaultCopiedToastMessage = intl.formatMessage({
      id: 'CopyButton.copiedToastMessage',
      defaultMessage: 'Copied',
      description: 'Toast message shown when content is copied',
    });

    const handleCopy = async () => {
      copy(copyText);
      toast({
        status: 'success',
        message: copiedToastMessage || defaultCopiedToastMessage,
      });
    };

    const ButtonComponent = disableText ? IconButton : Button;

    return (
      <ButtonComponent
        disabled={disabled}
        icon="clipboard"
        text={intl.formatMessage({
          id: 'CopyButton.buttonText',
          defaultMessage: 'Copy',
          description: 'Button text for copy action',
        })}
        size={size}
        tooltip={copyTooltip || defaultCopyTooltip}
        onClick={handleCopy}
      />
    );
  },
);
